import '../work-order.scss';
import { useContext, useEffect, useState } from 'react';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import {
  ScheduledService,
  ScheduledServiceProductUnit,
  UseabilityStatus,
  WorkOrderType,
} from '../../../clients/quipli-service/service-and-repair/service-and-repair';
import {
  Product,
  ProductInventoryUnit,
  ProductTrackingType,
  ProductType,
} from '../../../clients/quipli-service/product/product';
import { Spinner } from '../../../components/spinner/spinner';
import { SelectForm } from '../../../components/select-form/select-form';
import { Radio } from '../../../components/radio/radio';
import { Field, FieldLabel } from '../../../components/field/field';
import { toast } from '../../../util/toasts';
import { Typography } from '../../../components/typography/typography';
import { DateTime, Duration } from 'luxon';
import Icon from '../../../components/icons/icon';
import { NavLink, useParams } from 'react-router-dom';
import Container from '../../../components/container/container';
import './scheduled-service-page.scss';
import { groupBy } from 'lodash';
import { Collapsible } from '../../../components/collapsible/collapsible';
import { Button } from '../../../components/form/button';
import { Dropdown } from '../../../components/dropdown/dropdown';
import { TenantContext } from '../../../context/tenant-context';
import { ConfirmModal } from '../../../components/modal/confirm-modal';
import { DatePicker } from '../../../components/date-picker/date-picker';
import { Modal } from '../../../components/modal/modal';
import { SearchProduct } from '../../customer/page/components/search-product';

type ScheduledServiceProductUnitDetails = {
  productName: string;
  productUnitId: number;
  unitNumber?: string | undefined;
  serialNumber?: string | undefined;
  initialServiceDate?: DateTime;
  initialServiceHours?: number;
};

export const ScheduledServicePage = () => {
  const { scheduledServiceId } = useParams();
  const {
    listProducts,
    createScheduledService,
    updateScheduledService,
    getScheduledService,
    getConfiguration,
    listWorkOrderTypes,
    deleteScheduledService,
  } = useAPI();
  const { contextPath } = useContext(TenantContext);
  const contextPathUrl = contextPath && !contextPath.includes('legacy-app') ? contextPath : '/';

  // React states
  const [isLoading, setIsLoading] = useState<boolean>(true),
    [scheduledService, setScheduledService] = useState<ScheduledService | null>(),
    [isEditing, setIsEditing] = useState<boolean>(!scheduledServiceId),
    [isProductUnitModalOpen, setIsProductUnitModalOpen] = useState<boolean>(false),
    [products, setProducts] = useState<Product[]>([]),
    [serviceName, setServiceName] = useState<string>(scheduledService?.serviceName ?? ''),
    [workOrderTypeId, setWorkOrderTypeId] = useState<string>(scheduledService?.workOrderType.id ?? ''),
    [unitStatus, setUnitStatus] = useState<UseabilityStatus>(scheduledService?.unitStatus ?? UseabilityStatus.SoftDown),
    [serviceDurationDays, setServiceDurationDays] = useState<number>(),
    [machineHoursInterval, setMachineHoursInterval] = useState<number | undefined>(
      scheduledService?.machineHoursInterval,
    ),
    [frequencyDuration, setFrequencyDuration] = useState<{ value?: number; unit?: { id: string; label: string } }>({}),
    [selectedProductUnits, setSelectedProductUnits] = useState<ScheduledServiceProductUnitDetails[]>([]),
    [unitTrackingField, setUnitTrackingField] = useState<'unitNumber' | 'serialNumber'>('unitNumber'),
    [workOrderTypes, setWorkOrderTypes] = useState<WorkOrderType[]>([]),
    [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState<boolean>(false);

  // Callbacks
  const save = async () => {
    if (isSaveDisabled()) return;

    setIsLoading(true);
    try {
      let savedScheduledService;
      if (scheduledService) {
        savedScheduledService = await updateScheduledService(scheduledService.id, {
          serviceName,
          workOrderTypeId,
          unitStatus,
          duration: serviceDurationDays ? Duration.fromISO(`P${serviceDurationDays}D`) : undefined,
          machineHoursInterval,
          frequencyInterval:
            frequencyDuration.value && frequencyDuration.unit
              ? Duration.fromISO(`P${frequencyDuration.value}${frequencyDuration.unit.id}`)
              : undefined,
          productUnitIds: selectedProductUnits.map((unit) => unit.productUnitId),
          productUnits: selectedProductUnits.map((unit) => {
            return {
              productUnitId: unit.productUnitId,
              initialServiceDate: unit.initialServiceDate,
              initialServiceHours: unit.initialServiceHours,
            };
          }),
        });
      } else {
        savedScheduledService = await createScheduledService({
          serviceName,
          workOrderTypeId,
          unitStatus,
          duration: serviceDurationDays ? Duration.fromISO(`P${serviceDurationDays}D`) : undefined,
          machineHoursInterval,
          frequencyInterval:
            frequencyDuration.value && frequencyDuration.unit
              ? Duration.fromISO(`P${frequencyDuration.value}${frequencyDuration.unit.id}`)
              : undefined,
          productUnitIds: selectedProductUnits.map((unit) => unit.productUnitId),
          productUnits: selectedProductUnits.map((unit) => {
            return {
              productUnitId: unit.productUnitId,
              initialServiceDate: unit.initialServiceDate,
              initialServiceHours: unit.initialServiceHours,
            };
          }),
        });
      }
      toast('Scheduled service repair saved successfully', 'success');
      setScheduledService(savedScheduledService);
      setIsEditing(false);
    } catch (e) {
      toast('Error saving scheduled service repair', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const onUnitsSelected = (productUnitDetails: ScheduledServiceProductUnitDetails[]) => {
    const selectedUnits = [];
    for (const unitDetail of productUnitDetails) {
      let selectedUnit = selectedProductUnits.find((unit) => unit.productUnitId === unitDetail.productUnitId);
      if (selectedUnit) {
        selectedUnit.initialServiceDate = unitDetail.initialServiceDate;
        selectedUnit.initialServiceHours = unitDetail.initialServiceHours;
      } else {
        selectedUnit = unitDetail;
      }
      selectedUnits.push(selectedUnit);
    }

    setSelectedProductUnits([...selectedProductUnits, ...selectedUnits]);
  };

  const isSaveDisabled = (): boolean => {
    return (
      !serviceName ||
      !workOrderTypeId ||
      !unitStatus ||
      (!(frequencyDuration.value && frequencyDuration.unit) && !machineHoursInterval) ||
      selectedProductUnits.length === 0
    );
  };

  const removeScheduledService = async () => {
    if (scheduledService) {
      setIsLoading(true);
      try {
        await deleteScheduledService(scheduledService.id);
        toast('Scheduled service repair deleted successfully', 'success');
        window.location.href = `${contextPathUrl}manager/repair/scheduled-services/`;
      } catch (e) {
        toast('Failed to delete scheduled service repair', 'error');
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Hooks
  useEffect(() => {
    // Load products and work types
    const getContent = async () => {
      setIsLoading(true);

      const [_products, config, _scheduledService, _workOrderTypes] = await Promise.all([
        listProducts(),
        getConfiguration('CONF_UNIT_TRACKING_VIA'),
        scheduledServiceId ? getScheduledService(scheduledServiceId) : Promise.resolve(null),
        listWorkOrderTypes(),
      ]);
      setProducts(_products.filter((prod) => prod.trackingType === ProductTrackingType.SerialUnits));
      setSelectedProductUnits(
        Object.keys(scheduledService?.productUnits ?? {})
          .map((pu) => {
            return scheduledService!.productUnits[parseInt(pu)].units.map((unit: ScheduledServiceProductUnit) => ({
              productUnitId: unit.productUnit.id,
              unitNumber: unit.productUnit.unitNumber,
              serialNumber: unit.productUnit.serialNumber,
              initialServiceDate: unit.initialServiceDate,
              initialServiceHours: unit.initialServiceHours,
              productName: _products.find((prod) => prod.id === unit.productUnit.productId)?.name ?? '',
            }));
          })
          .flat(),
      );
      setUnitTrackingField(config?.value ? (config.value as 'unitNumber' | 'serialNumber') : 'unitNumber');
      setScheduledService(_scheduledService);
      setWorkOrderTypes(_workOrderTypes);
      setIsLoading(false);
    };

    getContent();
  }, []);

  useEffect(() => {
    if (scheduledService) {
      setServiceName(scheduledService.serviceName);
      setWorkOrderTypeId(scheduledService.workOrderType.id);
      setUnitStatus(scheduledService.unitStatus);
      if (scheduledService.duration) {
        setServiceDurationDays(scheduledService.duration.days);
      }
      if (scheduledService.machineHoursInterval) {
        setMachineHoursInterval(scheduledService.machineHoursInterval);
      }
      if (scheduledService.frequencyInterval) {
        const dur = scheduledService.frequencyInterval;
        setFrequencyDuration(
          dur.days
            ? { value: dur.days, unit: { id: 'D', label: 'Days' } }
            : dur.months
            ? { value: dur.months, unit: { id: 'M', label: 'Months' } }
            : dur.years
            ? { value: dur.years, unit: { id: 'Y', label: 'Years' } }
            : { value: dur.weeks, unit: { id: 'W', label: 'Weeks' } },
        );
      }
      setSelectedProductUnits(
        Object.keys(scheduledService?.productUnits ?? {})
          .map((pu) => {
            return scheduledService!.productUnits[parseInt(pu)].units.map((unit: ScheduledServiceProductUnit) => ({
              productUnitId: unit.productUnit.id,
              unitNumber: unit.productUnit.unitNumber,
              serialNumber: unit.productUnit.serialNumber,
              initialServiceDate: unit.initialServiceDate,
              initialServiceHours: unit.initialServiceHours,
              productName: products.find((prod) => prod.id === unit.productUnit.productId)?.name ?? '',
            }));
          })
          .flat(),
      );
    } else {
      setServiceName('');
      setWorkOrderTypeId('');
      setUnitStatus(UseabilityStatus.SoftDown);
      setServiceDurationDays(undefined);
      setMachineHoursInterval(undefined);
      setFrequencyDuration({});
      setSelectedProductUnits([]);
    }
  }, [scheduledService]);

  return (
    <div className="scheduled-service-page p-2 md:py-10 md:px-[5rem]">
      {isLoading || scheduledService === undefined ? (
        <Spinner />
      ) : (
        <Container className="p-3 md:p-10 scheduled-service-container">
          <div className="flex justify-between items-start">
            <div className="flex gap-5 items-end">
              <NavLink to={`/manager/repair/scheduled-services`}>
                <Icon icon="Back" />
              </NavLink>
              <Typography type="display-xs" weight="bold">
                {isEditing && !scheduledServiceId ? 'Create Scheduled Service' : 'Scheduled Service'}
              </Typography>
            </div>
            {!isEditing && scheduledServiceId && (
              <div>
                <Dropdown
                  trigger={() => (
                    <Button className="button-tertiary">
                      <Icon icon="DotsVertical" />
                    </Button>
                  )}
                >
                  <div
                    className="items-center gap-2"
                    onClick={() => {
                      setIsEditing(true);
                    }}
                  >
                    <Icon icon="Edit" size="medium" />
                    <Typography type="text-sm" className="text-quipli-black-800">
                      Edit
                    </Typography>
                  </div>
                  <div
                    className="items-center gap-2"
                    onClick={() => {
                      setShowConfirmDeleteModal(true);
                    }}
                  >
                    <Icon icon="Trash" size="medium" />
                    <Typography type="text-sm" className="text-quipli-black-800">
                      Delete
                    </Typography>
                  </div>
                </Dropdown>
              </div>
            )}
          </div>
          <div className="mt-4 grid grid-cols-1 gap-y-3">
            {/* Name */}
            <div className={`flex gap-5 items-start ${!isEditing ? 'disabled' : ''}`}>
              <div className="min-w-[120px]">
                <FieldLabel label="Name" isRequired={isEditing} />
              </div>
              {isEditing ? (
                <Field
                  isRequired={isEditing}
                  type="text"
                  value={serviceName ?? ''}
                  onChange={(v) => setServiceName(v)}
                />
              ) : (
                <Typography type="text-sm">{serviceName}</Typography>
              )}
            </div>

            {/* Work Type */}
            <div className={`flex gap-5 items-start ${!isEditing ? 'disabled' : ''}`}>
              <div className="min-w-[120px]">
                <FieldLabel label="Type" isRequired={isEditing} />
              </div>
              {isEditing ? (
                <div className="w-[200px]">
                  <SelectForm
                    menuShouldBlockScroll
                    menuPosition="fixed"
                    menuPortalTarget={document.body}
                    options={workOrderTypes}
                    value={workOrderTypes.find((t) => t.id === workOrderTypeId)}
                    onChange={(option) => setWorkOrderTypeId(option?.id ?? '')}
                    getOptionValue={(option) => option.id}
                    isLoading={isLoading}
                    isDisabled={isLoading || !isEditing}
                    isClearable={true}
                  />
                </div>
              ) : (
                <Typography type="text-sm">{workOrderTypes.find((t) => t.id === workOrderTypeId)?.label}</Typography>
              )}
            </div>

            {/* Unit Availability */}
            <div className={`work-order-field flex gap-3 items-start ${!isEditing ? 'disabled' : ''}`}>
              <div className="min-w-[120px]">
                <FieldLabel label="Unit Availability" isRequired={isEditing} />
              </div>
              {isEditing ? (
                <div className="flex gap-x-6 ml-2">
                  <div className="flex gap-x-1">
                    <Radio
                      name="unit_status"
                      type="white"
                      label={UseabilityStatus.SoftDown}
                      isChecked={unitStatus === UseabilityStatus.SoftDown}
                      onChange={(isChecked) => isChecked && setUnitStatus(UseabilityStatus.SoftDown)}
                    />
                  </div>
                  <div className="flex gap-x-1">
                    <Radio
                      name="unit_status"
                      type="white"
                      label={UseabilityStatus.HardDown}
                      isChecked={unitStatus === UseabilityStatus.HardDown}
                      onChange={(isChecked) => isChecked && setUnitStatus(UseabilityStatus.HardDown)}
                    />
                  </div>
                </div>
              ) : (
                <Typography type="text-sm">{unitStatus}</Typography>
              )}
            </div>

            {/* Service duration */}
            <div className="flex gap-4 items-center">
              <div className="min-w-[120px]">
                <FieldLabel label="Service Duration" isRequired={false} />
              </div>
              {isEditing ? (
                <div className="flex gap-2 items-center align-middle p-0 m-0">
                  <Field
                    type="number"
                    isRequired={false}
                    value={serviceDurationDays}
                    onChange={(v) => setServiceDurationDays(v)}
                  />
                  <span className="text-sm text-gray-500">days</span>
                </div>
              ) : (
                <Typography className="text-gray-800" type="text-sm">
                  {serviceDurationDays ? `${serviceDurationDays} days` : 'N/A'}
                </Typography>
              )}
            </div>

            {/* Recurrence */}
            <div>
              <FieldLabel label="Require service every: " isRequired={isEditing} />
              <div className="flex gap-4">
                <div className="mx-6 max-w-[50%] ">
                  <Typography className="p-0 m-0 text-gray-500" type="text-xs">
                    Duration
                  </Typography>
                  {isEditing ? (
                    <div className="flex gap-2 items-center align-middle p-0 m-0">
                      <Field
                        type="number"
                        isRequired={isEditing}
                        value={frequencyDuration?.value}
                        onChange={(v) => setFrequencyDuration({ ...frequencyDuration, value: v })}
                      />

                      <SelectForm
                        isRequired={false}
                        menuShouldBlockScroll
                        menuPosition="fixed"
                        menuPortalTarget={document.body}
                        options={[
                          { id: 'D', label: 'Days' },
                          { id: 'W', label: 'Weeks' },
                          { id: 'M', label: 'Months' },
                          { id: 'Y', label: 'Years' },
                        ]}
                        value={frequencyDuration?.unit}
                        onChange={(option) => {
                          if (option) setFrequencyDuration({ ...frequencyDuration, unit: option });
                        }}
                        getOptionValue={(option) => option.id}
                        getOptionLabel={(option) => option.label}
                      />
                    </div>
                  ) : (
                    <Typography className="text-gray-800" type="text-sm">
                      {frequencyDuration.value} {frequencyDuration.unit?.label}
                    </Typography>
                  )}
                </div>
                <p className="text-sm text-gray-500 mr-4">or</p>
                {/* machine hours interval */}
                <div>
                  <Typography type="text-xs" className="text-gray-500">
                    Machine Hours
                  </Typography>
                  {isEditing ? (
                    <Field
                      type="number"
                      isRequired={false}
                      value={machineHoursInterval ?? ''}
                      onChange={(v) => setMachineHoursInterval(v)}
                    />
                  ) : (
                    <Typography type="text-sm">{machineHoursInterval}</Typography>
                  )}
                </div>
              </div>
            </div>

            {/* Units */}
            <div className="flex flex-col gap-y-2">
              <div className="my-2">
                <FieldLabel label="Product Units" isRequired={true} />
                <div className="mx-3">
                  {Object.entries(groupBy(selectedProductUnits, 'productName')).map((productAndUnits) => {
                    const units = productAndUnits[1];
                    return (
                      <div className="py-2" key={productAndUnits[0]}>
                        <Collapsible
                          openAtStart={true}
                          trigger={(isOpen) => (
                            <div className="px-2 flex justify-between items-center cursor-pointer rounded-md">
                              <Typography type="text-sm">{units[0].productName}</Typography>
                              <div className="flex items-center gap-1">
                                {isEditing && (
                                  <Icon
                                    icon="Trash"
                                    onClick={() =>
                                      setSelectedProductUnits(selectedProductUnits.filter((u) => units.indexOf(u) < 0))
                                    }
                                  />
                                )}
                                <Button className="button-tertiary">
                                  <Icon icon={isOpen ? 'ChevronUp' : 'ChevronDown'} size="small" />
                                </Button>
                              </div>
                            </div>
                          )}
                        >
                          <div className="p-2 bg-gray-100 rounded-md grid grid-flow-col gap-7">
                            {units.map((unit) => (
                              <div className="bg-white rounded-md p-2 w-full flex justify-between gap-3 items-center">
                                <span className="text-sm">
                                  {unitTrackingField === 'unitNumber' ? unit.unitNumber : unit.serialNumber}
                                  {unit.initialServiceDate && Number.isInteger(unit.initialServiceHours)
                                    ? ` - Initial Service: ${unit.initialServiceDate?.toFormat('LLLL d, y')} / ${
                                        unit.initialServiceHours
                                      } hours`
                                    : unit.initialServiceDate
                                    ? ` - Initial Service: ${unit.initialServiceDate?.toFormat('LLLL d, y')}`
                                    : Number.isInteger(unit.initialServiceHours)
                                    ? ` - Initial Service: ${unit.initialServiceHours} hours`
                                    : ''}
                                </span>
                                {isEditing && (
                                  <Icon
                                    icon="X"
                                    size="small"
                                    onClick={() =>
                                      setSelectedProductUnits(
                                        selectedProductUnits.filter((u) => u.productUnitId !== unit.productUnitId),
                                      )
                                    }
                                  />
                                )}
                              </div>
                            ))}
                          </div>
                        </Collapsible>
                        <div className="w-full border-b-solid border-b"></div>
                      </div>
                    );
                  })}
                </div>
                {selectedProductUnits.length === 0 && (
                  <tr>
                    <td colSpan={3} className="py-2 text-center">
                      No units selected
                    </td>
                  </tr>
                )}
              </div>

              {isEditing && (
                <div>
                  <button
                    className="button button-small button-block button-primary"
                    disabled={false}
                    onClick={() => setIsProductUnitModalOpen(true)}
                  >
                    Add Unit
                  </button>
                </div>
              )}
            </div>

            {isEditing && (
              <div className="mt-4 flex justify-end">
                <button
                  className="button button-small button-block button-secondary mr-3"
                  disabled={isLoading}
                  onClick={() => setIsEditing(false)}
                >
                  Cancel
                </button>

                <button
                  className="button button-small button-block button-primary"
                  disabled={isSaveDisabled()}
                  onClick={() => save()}
                >
                  Save
                </button>
              </div>
            )}

            <ProductUnitModal
              isOpen={isProductUnitModalOpen}
              setIsOpen={setIsProductUnitModalOpen}
              unitTrackingField={unitTrackingField}
              existingSelectedProductUnitIds={selectedProductUnits.map((unit) => unit.productUnitId)}
              unitsSelected={onUnitsSelected}
            />

            <ConfirmModal
              isOpen={showConfirmDeleteModal}
              additionalText="Deleting this scheduled service will remove any associated future work orders."
              prompt="Are you sure you want to delete this scheduled service?"
              onConfirm={removeScheduledService}
              close={() => setShowConfirmDeleteModal(false)}
            />
          </div>
        </Container>
      )}
    </div>
  );
};

const ProductUnitModal = ({
  isOpen,
  setIsOpen,
  existingSelectedProductUnitIds,
  unitsSelected,
  unitTrackingField,
}: {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  existingSelectedProductUnitIds: number[];
  unitsSelected: (productUnitDetails: ScheduledServiceProductUnitDetails[]) => void;
  unitTrackingField: 'unitNumber' | 'serialNumber';
}) => {
  const [selectedProduct, setSelectedProduct] = useState<Product>();
  const [productUnitOptions, setProductUnitOptions] = useState<
    {
      productName: string;
      productUnitId: number;
      unitNumber?: string | undefined;
      serialNumber?: string | undefined;
    }[]
  >();
  const [selectedProductUnits, setSelectedProductUnits] = useState<
    { productName: string; productUnitId: number; unitNumber?: string | undefined; serialNumber?: string | undefined }[]
  >([]);
  const [initialServiceDate, setInitialServiceDate] = useState<DateTime | undefined>(undefined);
  const [initialServiceHours, setInitialServiceHours] = useState<number | undefined>();

  const onProductSelected = (product: Product | undefined) => {
    setProductUnitOptions(undefined);
    // If product is selected, the unit list is also updated

    if (!product) {
      setSelectedProduct(undefined);
      return;
    }

    setSelectedProduct(product);
    const unitOptions = product.inventoryUnits
      .filter(
        (unit) =>
          unit.isActive &&
          unit.deletedAt === undefined &&
          !selectedProductUnits.find((u) => u.productUnitId === unit.id) &&
          !existingSelectedProductUnitIds.includes(unit.id),
      )
      .map((unit) => ({ ...unit, productName: product.name, productUnitId: unit.id }));

    setProductUnitOptions(unitOptions);
  };

  const onUnitsSelected = () => {
    const units = selectedProductUnits.map((unit) => {
      return {
        productName: unit.productName,
        productUnitId: unit.productUnitId,
        unitNumber: unit.unitNumber,
        serialNumber: unit.serialNumber,
        initialServiceDate: initialServiceDate,
        initialServiceHours: initialServiceHours,
      };
    });
    unitsSelected(units);
    closeModal();
  };

  const isAddButtonDisabled = (): boolean => {
    return selectedProduct !== undefined && selectedProductUnits.length === 0;
  };

  const closeModal = () => {
    setSelectedProduct(undefined);
    setProductUnitOptions(undefined);
    setSelectedProductUnits([]);
    setInitialServiceDate(undefined);
    setInitialServiceHours(undefined);
    setIsOpen(false);
  };

  return (
    <Modal
      isOpen={isOpen}
      title="Add Product Unit"
      actions={
        <>
          <button className="button button-small button-block button-secondary mt-4" onClick={() => closeModal()}>
            Cancel
          </button>

          <button
            className="button button-small button-block button-primary mt-4"
            disabled={isAddButtonDisabled()}
            onClick={() => onUnitsSelected()}
          >
            Add
          </button>
        </>
      }
      close={closeModal}
    >
      {/* Product */}
      <div>
        <FieldLabel label="Product:" isRequired />
        <SearchProduct
          value={selectedProduct}
          onSelect={onProductSelected}
          productType={ProductType.Product}
          productTrackingType={ProductTrackingType.SerialUnits}
          placeholder={'Search products'}
        />
      </div>

      {/* Unit */}
      <SelectForm
        label="Unit:"
        isRequired={true}
        menuShouldBlockScroll
        menuPosition="fixed"
        menuPortalTarget={document.body}
        options={[
          ...(productUnitOptions?.length
            ? [{ productName: 'All Units', unitNumber: undefined, serialNumber: undefined, productUnitId: 0 }]
            : []),
          ...(productUnitOptions ?? []),
        ]}
        //value={null}
        getOptionLabel={(option) =>
          !option
            ? ''
            : option.productName === 'All Units'
            ? 'All Units'
            : unitTrackingField === 'unitNumber'
            ? option.unitNumber!
            : option.serialNumber!
        }
        isClearable={true}
        isDisabled={selectedProduct === null}
        onChange={(option) => {
          if (option?.productName === 'All Units') {
            setSelectedProductUnits([
              ...selectedProductUnits,
              ...(productUnitOptions ?? []).filter(
                (unit) =>
                  !selectedProductUnits.find((u) => u.productUnitId === unit.productUnitId) &&
                  !existingSelectedProductUnitIds.includes(unit.productUnitId),
              ),
            ]);
          } else {
            setSelectedProductUnits([
              ...selectedProductUnits,
              option as ProductInventoryUnit & { productName: string; productUnitId: number },
            ]);
          }
        }}
      />

      {/* Initial Service */}
      <div>
        <FieldLabel label="Initial Service:" isRequired={false} />
        <div className=" gap-4">
          <div className="mx-6 max-w-[50%]">
            <Typography className="p-0 m-0 text-gray-500" type="text-xs">
              Date
            </Typography>

            <div className="flex gap-2 items-center align-middle p-0 m-0">
              <DatePicker isRequired={false} value={initialServiceDate} onChange={setInitialServiceDate} />
            </div>
          </div>
          {(!selectedProduct || selectedProduct.allowHourTracking) && (
            <>
              <div className="mx-6">
                <p className="text-sm text-gray-500 mr-4">or</p>
              </div>
              {/* machine hours interval */}
              <div className="mx-6 max-w-[50%]">
                <Typography type="text-xs" className="text-gray-500">
                  Machine Hours
                </Typography>
                <Field
                  type="number"
                  isRequired={false}
                  value={initialServiceHours ?? ''}
                  onChange={(v) => setInitialServiceHours(v)}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};
